import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * The Speed to Lead agent's screens. A lead's number arrives with its middle
 * hidden in lists, and in full only when a lead is opened. Times are UTC ISO
 * strings.
 */

export type StlStatus =
  | 'new' | 'waiting' | 'calling' | 'transferred' | 'scheduled' | 'follow_up' | 'closed' | 'needs_person';

export interface StlLeadCard {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  arrived_at: string;
  status: StlStatus;
  calls: number;
  next_call_at: string | null;
  emailed: boolean;
  outcome: string | null;
  callback_at: string | null;
  /** Who has it in AgencyZoom, once booked. */
  specialist_name: string | null;
  in_agencyzoom: boolean;
  needs_person_reason: string | null;
  completed_at: string | null;
  /** While a call is under way: "ringing", "in progress", "ended". */
  call_status: string | null;
  /** The specialist's phone is ringing for this lead now. */
  handing_over: boolean;
}

export interface StlTurn { role: 'agent' | 'user'; seconds: number; message: string }

export interface StlCallRecord {
  number: number;
  started_at: string;
  ended_at: string | null;
  status: string;
  outcome: string | null;
  duration_secs: number | null;
  summary: string | null;
  transcript: StlTurn[];
  transfer_started_at: string | null;
  transfer_taken_at: string | null;
  transfer_missed_at: string | null;
}

export interface StlLeadDetail extends StlLeadCard {
  phone_full: string | null;
  hubspot_contact_id: number;
  agencyzoom_url: string | null;
  steps: { kind: string; at: string; title: string; detail: string | null }[];
  call_records: StlCallRecord[];
}

export interface StlDay { day: string; value: number }

export interface StlSpotlight {
  flow: {
    agency: string; assistant: string; specialist_phone: string | null;
    transfer_line: string | null; call_after_seconds: number; email_domains: string[]; chat_minutes: number;
  };
  totals: {
    leads: number; emailed: number; called: number; transferred: number; scheduled: number; follow_up: number;
    closed: number; needs_person: number; working: number; median_minutes_to_call: number | null;
  };
  leads_by_day: StlDay[];
  transferred_by_day: StlDay[];
  latest: StlLeadCard[];
}

export interface StlSettings {
  agency_name: string;
  assistant_name: string;
  specialist_phone: string | null;
  specialist_az_id: number | null;
  transfer_line: string | null;
  email_domains: string[];
  call_after_seconds: number;
  retry_after_minutes: number;
  max_calls: number;
  time_zone: string;
  chat_minutes: number;
  ring_seconds: number;
  reply_to: string | null;
  az_pipeline_id: number | null;
  az_stage_id: number | null;
  az_source_id: number | null;
  welcome_subject: string;
  welcome_body: string;
  scheduled_subject: string;
  scheduled_body: string;
  follow_up_subject: string;
  follow_up_body: string;
}

export interface StlConfiguration {
  settings: StlSettings;
  team: { id: number; name: string; email: string | null }[];
  team_error: string | null;
  /** Why it cannot call right now, in words; null when it can. */
  blocked: string | null;
  /** The first thing the AI says, for a lead called Alex. */
  opening: string;
  rules: { when: string; then: string; detail: string }[];
  guardrails: { title: string; detail: string }[];
  /** Only after a save: the settings were kept but ElevenLabs was not updated. */
  voice_error?: string | null;
}

export interface StlIntegration {
  id: 'hubspot' | 'voice' | 'transfer' | 'agencyzoom' | 'email';
  name: string;
  role: string;
  detail: string;
  connected: boolean;
  error: string | null;
  activity: string | null;
  last_run_at: string | null;
  job_error: string | null;
}

type SettingsChange = Partial<Omit<StlSettings, 'az_pipeline_id' | 'az_stage_id' | 'az_source_id'>>;

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) };
}

function prefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return `${tenantApiPrefix(tid)}/speed-to-lead`;
}

async function call<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const response = await fetch(buildApiUrl(`${prefix()}${path}`), { ...init, headers: getAuthHeaders() });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallback);
  }
  return response.json();
}

/** `live`: leads still being worked. `done`: finished ones. */
export const fetchStlLeads = (scope: 'live' | 'done') =>
  call<{ leads: StlLeadCard[] }>(`/leads?scope=${scope}`, { method: 'GET' }, 'Could not load leads');
export const fetchStlLead = (id: string) =>
  call<StlLeadDetail>(`/leads/${id}`, { method: 'GET' }, 'Could not load that lead');
export const fetchStlSpotlight = () => call<StlSpotlight>('/spotlight', { method: 'GET' }, 'Could not load Spotlight');
export const fetchStlConfiguration = () =>
  call<StlConfiguration>('/configuration', { method: 'GET' }, 'Could not load how the agent is set up');
export const saveStlSettings = (change: SettingsChange) =>
  call<StlConfiguration>('/settings', { method: 'PATCH', body: JSON.stringify(change) }, 'Could not save the settings');
export const fetchStlIntegrations = () =>
  call<{ integrations: StlIntegration[] }>('/integrations', { method: 'GET' }, 'Could not load integrations');

/** Listen for "the leads changed" until `signal` aborts. Carries no data. */
export async function watchStlLeads(onChange: () => void, signal: AbortSignal, onOpen?: () => void): Promise<void> {
  const response = await fetch(buildApiUrl(`${prefix()}/leads/stream`), {
    headers: { ...getAuthHeaders(), Accept: 'text/event-stream' }, signal,
  });
  if (!response.ok || !response.body) throw new Error(`Live updates unavailable (${response.status})`);
  onOpen?.();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let end = buffer.indexOf('\n\n');
    while (end !== -1) {
      if (buffer.slice(0, end).split('\n').includes('event: changed')) onChange();
      buffer = buffer.slice(end + 2);
      end = buffer.indexOf('\n\n');
    }
  }
}
